"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import ThemeSwitch from "@/components/ThemeSwitch";
import { contact, d, img, menu } from "@/lib/site";

const phoneIcon = (
  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
  </svg>
);

function isCurrent(href: string, path: string) {
  return href === "/" ? path === "/" : path.startsWith(href.replace(/\/$/, ""));
}

// Sticky header: wordmark with the chair emblem, the five menu items, phone pill and theme switch.
// Once scrolled it gets compact; below 960 px the menu sits behind a burger.
export default function SiteHeader() {
  const path = usePathname();
  const [small, setSmall] = useState(false);
  const [open, setOpen] = useState(false);
  // height of the full-size header: the sticky box keeps it, so shrinking never moves the page content
  const [full, setFull] = useState<number>();
  const ref = useRef<HTMLElement>(null);

  // Current visible height as --header-h on the sticky bars under the header (gallery filters, marked
  // data-under-header); follows the shrink animation frame by frame. Set on those elements only: a changed custom
  // property on <html> re-styles the whole page every frame and made the shrink stutter.
  const applyHeight = () => {
    const h = ref.current?.offsetHeight;
    if (h) document.querySelectorAll<HTMLElement>("[data-under-header]").forEach((e) => e.style.setProperty("--header-h", `${h}px`));
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(applyHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // a new page may bring its own bar
  useEffect(applyHeight, [path]);

  // Measured only while full size and settled: during the grow animation it would report the in-between
  // heights and the box would push the page down. Window resizes measure again (the menu may wrap).
  useEffect(() => {
    const el = ref.current;
    if (!el || small || open) return;
    const measure = () => setFull(el.offsetHeight);
    // first render is always full size (also when the page loads scrolled down): take it right away
    if (full === undefined) measure();
    const settle = setTimeout(measure, 250);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(settle);
      window.removeEventListener("resize", measure);
    };
  }, [small, open, full]);

  useEffect(() => {
    const onScroll = () => setSmall(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // a new page closes the menu
  const [lastPath, setLastPath] = useState(path);
  if (path !== lastPath) {
    setLastPath(path);
    setOpen(false);
  }

  return (
    // The sticky box keeps the full height; the header inside shrinks over the content (the empty rest lets
    // clicks through), so the page doesn't move when it gets compact.
    <div className="pointer-events-none sticky top-0 z-30" style={{ height: full, viewTransitionName: "site-header" }}>
      <header ref={ref} className={`pointer-events-auto border-b border-line bg-bg transition-shadow duration-200 ${small ? "shadow-[0_6px_20px_rgba(0,0,0,.08)]" : ""}`}>
        <div className={`mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-4 transition-[padding] duration-200 ease-out min-[521px]:px-6 ${small ? "py-1" : "py-[18px]"}`}>
          {/* compact: the logo scales down (CSS scale, no text re-layout); its box shrinks with it by the
              negative margin, so the bar gets lower too */}
          <Link
            href="/"
            className={`flex origin-left items-start gap-1 pt-3 no-underline transition-[scale,margin] duration-200 ease-out ${small ? "-my-2 scale-[.8]" : ""}`}
          >
            <span className="flex flex-col leading-[1.1]">
              <span className="font-display text-[28px] font-bold tracking-[-0.02em]">
                ideen<span className="text-accent">sprung</span>
              </span>
              {/* w-0: the line may run wider than "ideensprung" without pushing the emblem away */}
              <span className="block w-0 overflow-visible text-[12px] tracking-[0.14em] whitespace-nowrap text-ink-3 uppercase">Polsterei · Filderstadt</span>
            </span>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={d(img.emblem)} alt="" className="-mt-4 size-10" />
          </Link>

          <nav aria-label="Hauptnavigation" className="hidden flex-wrap gap-x-5 gap-y-1 text-[17px] font-medium min-[961px]:flex min-[1181px]:gap-x-8 min-[1181px]:text-[19px]">
            {menu.map((m) => {
              const cur = isCurrent(m.href, path);
              return (
                <Link
                  key={m.href}
                  href={m.href}
                  aria-current={cur ? "page" : undefined}
                  className={`py-2.5 no-underline hover:text-accent ${cur ? "text-accent shadow-[inset_0_-2px_0_var(--accent)]" : ""}`}
                >
                  {m.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 min-[521px]:gap-3">
            <a
              href={contact.tel}
              className="hidden rounded-full bg-accent-fill px-5 py-3 text-[15px] font-semibold text-white no-underline min-[521px]:block"
            >
              {contact.phone}
            </a>
            <a
              href={contact.tel}
              aria-label={`Anrufen: ${contact.phone}`}
              className="flex size-11 items-center justify-center rounded-xl bg-accent-fill text-white min-[521px]:hidden"
            >
              {phoneIcon}
            </a>
            <div className="hidden min-[961px]:block">
              <ThemeSwitch />
            </div>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={open}
              className="flex size-11 cursor-pointer items-center justify-center rounded-xl border-[1.5px] border-line-strong min-[521px]:size-12 min-[961px]:hidden"
            >
              <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d={open ? "M6 6l12 12M18 6 6 18" : "M4 7h16M4 12h16M4 17h16"} />
              </svg>
            </button>
          </div>
        </div>

        {open && (
          <nav aria-label="Hauptnavigation" className="flex flex-col border-t border-line px-6 pt-1 pb-6 font-medium min-[961px]:hidden">
            {menu.map((m) => {
              const cur = isCurrent(m.href, path);
              return (
                <Link
                  key={m.href}
                  href={m.href}
                  onClick={() => setOpen(false)}
                  aria-current={cur ? "page" : undefined}
                  className={`border-b border-line py-3.5 text-[20px] no-underline ${cur ? "text-accent" : ""}`}
                >
                  {m.label}
                </Link>
              );
            })}
            <a href={contact.tel} className="mt-4 rounded-full bg-accent-fill px-5 py-3.5 text-center font-semibold text-white no-underline">
              {contact.phone} anrufen
            </a>
            <div className="mt-4 flex items-center gap-3 text-[16px] text-ink-3">
              <ThemeSwitch />
              Hell / Dunkel
            </div>
          </nav>
        )}
      </header>
    </div>
  );
}
